import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// A4 dimensions in points
const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;
const MARGIN_PT = 20;
const USABLE_HEIGHT_PT = A4_HEIGHT_PT - 2 * MARGIN_PT;
const CANVAS_SCALE = 2;

export class PDFExporter {
  /**
   * Export the resume card as a multi-page A4 PDF.
   * @param {HTMLElement} element - The resume card DOM element
   * @param {Object} options
   * @param {string} options.fileName - Download filename (default: 'runners-resume.pdf')
   * @returns {Promise<void>}
   */
  static async export(element, { fileName = 'runners-resume.pdf' } = {}) {
    // 1. Wait for fonts and images
    await document.fonts.ready;
    await PDFExporter._waitForImages(element);

    // 2. Pre-convert cross-origin images to data URLs
    const originalSrcs = await PDFExporter._convertImagesToDataURLs(element);

    try {
      // 3. Collect break-point Y-positions from the DOM before rendering
      const breakPoints = PDFExporter._collectBreakPoints(element);

      // 4. Render to canvas
      const canvas = await html2canvas(element, {
        backgroundColor: null,
        scale: CANVAS_SCALE,
        useCORS: true,
        allowTaint: true,
        logging: false,
        onclone: (clonedDoc) => {
          const clonedEl = clonedDoc.getElementById(element.id)
            || clonedDoc.querySelector('.tpl-classic, .tpl-magazine');
          if (clonedEl) clonedEl.classList.add('export-mode');
        },
      });

      // 5. Calculate scale: fit card width to A4 width minus margins
      const contentWidthPt = A4_WIDTH_PT - 2 * MARGIN_PT;
      const scaleFactor = contentWidthPt / canvas.width;
      const canvasHeightPt = canvas.height * scaleFactor;

      // 6. If it all fits on one page, simple single-page PDF
      if (canvasHeightPt <= USABLE_HEIGHT_PT) {
        const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', MARGIN_PT, MARGIN_PT, contentWidthPt, canvasHeightPt);
        pdf.save(fileName);
        return;
      }

      // 7. Multi-page: compute slice boundaries
      const slices = PDFExporter._computePageSlices(
        breakPoints, canvas.height, scaleFactor
      );

      // 8. Assemble PDF
      const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
      const totalPages = slices.length;

      for (let i = 0; i < slices.length; i++) {
        if (i > 0) pdf.addPage();

        const { startPx, endPx } = slices[i];
        const sliceHeightPx = endPx - startPx;

        // Crop the canvas region
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, startPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);

        const sliceImg = sliceCanvas.toDataURL('image/png');
        const sliceHeightPt = sliceHeightPx * scaleFactor;
        pdf.addImage(sliceImg, 'PNG', MARGIN_PT, MARGIN_PT, contentWidthPt, sliceHeightPt);

        // Page footer (only if multi-page)
        if (totalPages > 1) {
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          pdf.text(
            `Page ${i + 1} of ${totalPages}`,
            A4_WIDTH_PT / 2,
            A4_HEIGHT_PT - 10,
            { align: 'center' }
          );
        }
      }

      pdf.save(fileName);
    } finally {
      // 9. Restore original image srcs
      PDFExporter._restoreImageSrcs(element, originalSrcs);
    }
  }

  // -------------------------------------------------------------------------
  // Break-point detection
  // -------------------------------------------------------------------------

  /**
   * Collect Y-positions (in element-local pixels) where page breaks are safe.
   * Safe positions: after the stats row, between race rows, before year headers.
   */
  static _collectBreakPoints(element) {
    const elementRect = element.getBoundingClientRect();
    const points = [];

    // After stats section
    const statsRow = element.querySelector('.tpl-classic__stats');
    if (statsRow) {
      const r = statsRow.getBoundingClientRect();
      points.push({ y: r.bottom - elementRect.top, type: 'after-stats' });
    }

    // Before each year header
    const yearHeaders = element.querySelectorAll('.tpl-classic__year-header');
    yearHeaders.forEach((el) => {
      const r = el.getBoundingClientRect();
      points.push({ y: r.top - elementRect.top, type: 'before-year' });
    });

    // After each race row
    const raceRows = element.querySelectorAll('.tpl-classic__race-row');
    raceRows.forEach((el) => {
      const r = el.getBoundingClientRect();
      points.push({ y: r.bottom - elementRect.top, type: 'after-race' });
    });

    // Generic fallback: if no template-specific breaks found, try common patterns
    if (points.length === 0) {
      // Try any element with 'race-row' or 'race' in the class name
      const genericRows = element.querySelectorAll('[class*="race-row"], [class*="race_row"]');
      genericRows.forEach((el) => {
        const r = el.getBoundingClientRect();
        points.push({ y: r.bottom - elementRect.top, type: 'after-race' });
      });
    }

    // Sort by Y position and deduplicate
    points.sort((a, b) => a.y - b.y);
    return points;
  }

  // -------------------------------------------------------------------------
  // Page-slice computation (pure logic)
  // -------------------------------------------------------------------------

  /**
   * Given break points and canvas dimensions, compute which pixel ranges
   * go on which page.
   *
   * @param {Array<{y: number, type: string}>} breakPoints - DOM Y positions
   * @param {number} canvasHeight - total canvas height in pixels
   * @param {number} scaleFactor - maps canvas pixels to PDF points
   * @returns {Array<{startPx: number, endPx: number}>}
   */
  static _computePageSlices(breakPoints, canvasHeight, scaleFactor) {
    // Convert DOM positions to canvas pixels (html2canvas scale is 2x)
    const domToCanvas = CANVAS_SCALE; // html2canvas scale factor

    const maxSliceHeightPx = USABLE_HEIGHT_PT / scaleFactor;
    const slices = [];
    let currentStart = 0;

    // Convert break points to canvas pixel coordinates
    const canvasBreaks = breakPoints.map((bp) => ({
      y: bp.y * domToCanvas,
      type: bp.type,
    }));

    // Add the end of canvas as a final break point
    canvasBreaks.push({ y: canvasHeight, type: 'end' });

    let lastBreak = 0;

    for (const bp of canvasBreaks) {
      const sliceHeight = bp.y - currentStart;

      if (sliceHeight > maxSliceHeightPx && lastBreak > currentStart) {
        // Cut at the previous safe break
        slices.push({ startPx: currentStart, endPx: lastBreak });
        currentStart = lastBreak;
      } else if (sliceHeight > maxSliceHeightPx && lastBreak <= currentStart) {
        // No safe break available — hard-cut at page boundary
        const hardCut = currentStart + maxSliceHeightPx;
        slices.push({ startPx: currentStart, endPx: hardCut });
        currentStart = hardCut;
      }

      lastBreak = bp.y;
    }

    // Push the final slice
    if (currentStart < canvasHeight) {
      slices.push({ startPx: currentStart, endPx: canvasHeight });
    }

    return slices;
  }

  // -------------------------------------------------------------------------
  // Image helpers (mirrors PNGExporter)
  // -------------------------------------------------------------------------

  static async _waitForImages(element) {
    const images = element.querySelectorAll('img');
    await Promise.all(Array.from(images).map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
    }));
  }

  static async _convertImagesToDataURLs(element) {
    const images = element.querySelectorAll('img');
    const originalSrcs = new Map();

    await Promise.all(Array.from(images).map(async (img) => {
      if (!img.src || !img.src.startsWith('http')) return;
      try {
        const imgUrl = new URL(img.src);
        if (imgUrl.origin === window.location.origin) return;
      } catch { return; }

      try {
        originalSrcs.set(img, img.src);
        const response = await fetch(img.src, { mode: 'cors' });
        const blob = await response.blob();
        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        img.src = dataUrl;
      } catch {
        originalSrcs.delete(img);
      }
    }));

    return originalSrcs;
  }

  static _restoreImageSrcs(element, originalSrcs) {
    for (const [img, src] of originalSrcs) {
      img.src = src;
    }
  }
}
